import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { FaChevronLeft, FaChevronRight, FaStar, FaQuoteLeft } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { optimizeImage } from "../utils/imageOptimizer";
import "./Testimonials.css";

const testimonials = [
  {
    quote: "I recently tried Canine Crown wet food for my cats, and overall I had a good experience with it. The gravy texture is smooth and appealing, especially for picky eaters who prefer wet food over dry kibble. My cats enjoyed the Chicken Adult Gravy variant and finished it quickly without hesitation.What I liked most is that the ingredients list includes real chicken, chicken broth, pumpkin puree, taurine, vitamins, and omega-3 sources, which are important for digestion, coat health, heart function, and overall wellness. The moisture content is also good, helping with hydration.The food smells fresh compared",
    name: "Habib, Olive Feline's & Exotics, Chennai",
    designation: "Pet Parent",
    src: "https://caninecrown.ascentbw.com/Image/Home/Tes1.webp",
    rating: 5,
  },
  {
    quote: "Finding the perfect cat food can be tough with so many options out there. After searching for a long time, I found Canine Crown a fantastic, India made food option that offers top-tier nutrition. I use it for all of my exotic cats and kittens, and they love the taste! More importantly, the health results have been incredible because it is packed with the vital nutrients they need.",
    name: "Mohammed Ashraf, Afnapersians & Exotics, Chennai",
    designation: "Pet Parent",
    src: "https://caninecrown.ascentbw.com/Image/Home/Tes4.webp",
    rating: 5,
  },
  {
    quote: "Customers regularly ask for Canine Crown products because of their quality and variety. The response from pet parents has been very positive, and many customers return to purchase again. The brand has built strong trust among pet owners, especially for its consistent quality and nutrition. From a retailer's perspective, Canine Crown is a dependable product range that delivers satisfaction and value.",
    name: " Ramesh Kumar",
    designation: "Pet Store Owner",
    src: "https://caninecrown.ascentbw.com/Image/Home/Test3.webp",
    rating: 5,
  },
  {
    quote: "I've tried countless food toppers over the years, but nothing compares to Canine Crown. The moment I open that container, my dogs come running from every corner of the house—tails wagging, eyes locked on, full-on drooling before the bowl even hits the floor. What I love most is how excited mealtime has become. My picky eater Polo, now cleans her bowl in seconds, and has more enthusiasm. It's become a non-negotiable in our household. ",
    name: " Ragavi ",
    designation: "Pet Parent",
    src: "https://caninecrown.ascentbw.com/Image/Home/Tes2.webp",
    rating: 5,
  }
];

const StarRating = ({ rating }) => {
  return (
    <div className="testimonial-stars">
      {[...Array(5)].map((_, i) => (
        <FaStar
          key={i}
          className={i < rating ? "star-filled" : "star-empty"}
        />
      ))}
    </div>
  );
};

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const autoplayIntervalRef = useRef(null);

  const testimonialsLength = useMemo(() => testimonials.length, []);
  const currentTestimonial = useMemo(() => testimonials[currentIndex], [currentIndex]);

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonialsLength);
    if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
  }, [testimonialsLength]);

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonialsLength) % testimonialsLength);
    if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
  }, [testimonialsLength]);

  const goToSlide = useCallback((index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
  }, [currentIndex]);

  useEffect(() => {
    autoplayIntervalRef.current = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonialsLength);
    }, 6000);
    return () => {
      if (autoplayIntervalRef.current) clearInterval(autoplayIntervalRef.current);
    };
  }, [testimonialsLength]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handlePrev, handleNext]);

  const slideVariants = {
    hidden: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    visible: {
      x: '0%',
      opacity: 1,
      transition: { type: 'spring', stiffness: 200, damping: 25 },
    },
    exit: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      transition: { type: 'spring', stiffness: 200, damping: 25 },
    }),
  };

  return (
    <section className="testimonials-section">
      <div className="testimonials-container-new">
        <div className="testimonial-header-new">
          <h2 className="testimonials-title-new">Loved by Pets, Trusted by Families</h2>
          <p className="testimonials-subtitle-new">See how Canine Crown supports healthier and happier pets every day.</p>
        </div>

        <div className="testimonial-slider-wrapper">
          <div className="testimonial-slider-inner">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="testimonial-slide-motion"
              >
                <div className="testimonial-slide-content">
                  {/* Left Column: Image with Overlap */}
                  <div className="testimonial-img-wrapper">
                    <img
                      src={optimizeImage(currentTestimonial.src, { width: 500, height: 500 })}
                      alt={currentTestimonial.name}
                      className="testimonial-slide-img"
                      loading="lazy"
                      width="500"
                      height="500"
                    />
                  </div>

                  {/* Right Column: Card */}
                  <div className="testimonial-card-content">
                    <FaQuoteLeft className="testimonial-quote-icon" aria-hidden="true" />
                    <blockquote className="testimonial-quote-text">
                      {currentTestimonial.quote}
                    </blockquote>
                    
                    <StarRating rating={currentTestimonial.rating} />

                    <div className="testimonial-card-footer">
                      <div className="testimonial-author-info">
                        <h4 className="testimonial-author-name">{currentTestimonial.name}</h4>
                        <p className="testimonial-author-role">{currentTestimonial.designation}</p>
                      </div>

                      {/* Navigation Controls inside footer */}
                      <div className="testimonial-nav-buttons">
                        <button
                          onClick={handlePrev}
                          className="testimonial-nav-btn"
                          aria-label="Previous testimonial"
                        >
                          <FaChevronLeft size={16} />
                        </button>
                        <button
                          onClick={handleNext}
                          className="testimonial-nav-btn"
                          aria-label="Next testimonial"
                        >
                          <FaChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dot Indicators */}
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`testimonial-dot ${currentIndex === index ? 'active' : ''}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
